// ============================================================================
// PostingEditPanel — panneau « Modifier l'affichage » (E1d, F-306)
// ============================================================================
// Portage maison du PoC posting-editor.tsx au standard fiche routee : panneau
// collapsible (details/summary, gabarit EditCommitteePanel E3a) plutot qu'un dialog
// — l'edition vit dans la fiche /affichages/:id. Champs M-300 + LOT A diffusion
// (lien externe AJOUTABLE PLUS TARD). Mutation useUpdatePosting. Visible si
// viewer.canWrite (M-301, garde re-verifiee serveur).

import { useState } from 'react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  useUpdatePosting, postingErrorCode, type PostingDetail,
  type PostingVisibility, type PostingExternalStatus,
} from '../../hooks/usePostings';
import { TasksFields } from './TasksFields';


export function PostingEditPanel({
  posting,
  onError,
}: {
  posting: PostingDetail;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdatePosting(posting.id);

  const [title, setTitle] = useState(posting.title);
  const [description, setDescription] = useState(posting.description ?? '');
  const [tasks, setTasks] = useState<string[]>(posting.tasks.length > 0 ? posting.tasks : ['']);
  const [deadline, setDeadline] = useState(posting.deadline ?? '');
  const [visibility, setVisibility] = useState<PostingVisibility>(posting.visibility ?? 'internal');
  const [externalStatus, setExternalStatus] = useState<PostingExternalStatus | ''>(
    posting.externalStatus ?? '',
  );
  const [externalUrl, setExternalUrl] = useState(posting.externalUrl ?? '');
  const [positionsCount, setPositionsCount] = useState(posting.positionsCount ?? 1);
  const [stepError, setStepError] = useState<string | null>(null);

  function save() {
    if (!title.trim()) {
      onError('invalid');
      return;
    }
    if (visibility === 'internal' && (externalStatus || externalUrl.trim())) {
      setStepError(t('gest:affichages.form.diffusionInternalConflict'));
      return;
    }
    if (visibility !== 'internal' && !externalStatus) {
      setStepError(t('gest:affichages.form.externalStatusRequired'));
      return;
    }
    if (externalStatus === 'published' && !externalUrl.trim()) {
      setStepError(t('gest:affichages.form.externalUrlRequired'));
      return;
    }
    const count = Number(positionsCount);
    if (!Number.isInteger(count) || count < 1 || count > 99) {
      setStepError(t('gest:affichages.form.positionsCountInvalid'));
      return;
    }
    setStepError(null);
    update.mutate(
      {
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        tasks: tasks.map((task) => task.trim()).filter((task) => task.length > 0).slice(0, 3),
        deadline: deadline || null,
        visibility,
        externalStatus: visibility === 'internal' ? null : (externalStatus || null),
        externalUrl: visibility === 'internal'
          ? null
          : (externalUrl.trim() || null),
        positionsCount: count,
      },
      { onError: (err) => onError(postingErrorCode(err)) },
    );
  }

  return (
    <details className={`${GS_COMPONENTS.card} group px-4 py-3`}>
      <summary className="cursor-pointer select-none text-field font-semibold text-slate-700 dark:text-slate-200">
        {t('gest:affichages.edit.summary')}
      </summary>
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="edit-posting-title" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.postTitle')}
          </label>
          <input
            id="edit-posting-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className={`${GS_COMPONENTS.input}`}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="edit-posting-positions" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.positionsCount')}
          </label>
          <input
            id="edit-posting-positions"
            type="number"
            min={1}
            max={99}
            value={positionsCount}
            onChange={(e) => setPositionsCount(Number(e.target.value))}
            className={`${GS_COMPONENTS.input} w-32`}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="edit-posting-visibility" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.visibility')}
          </label>
          <select
            id="edit-posting-visibility"
            value={visibility}
            onChange={(e) => {
              const v = e.target.value as PostingVisibility;
              setVisibility(v);
              if (v === 'internal') {
                setExternalStatus('');
                setExternalUrl('');
              }
            }}
            className={`${GS_COMPONENTS.input}`}
          >
            <option value="internal">{t('gest:affichages.diffusion.internal')}</option>
            <option value="external">{t('gest:affichages.diffusion.external')}</option>
            <option value="both">{t('gest:affichages.diffusion.both')}</option>
          </select>
        </div>
        {visibility !== 'internal' ? (
          <div className="flex flex-col gap-2">
            <label htmlFor="edit-posting-external-status" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:affichages.form.externalStatus')}
            </label>
            <select
              id="edit-posting-external-status"
              value={externalStatus}
              onChange={(e) => setExternalStatus(e.target.value as PostingExternalStatus | '')}
              className={`${GS_COMPONENTS.input}`}
            >
              <option value="" disabled>
                {t('gest:affichages.form.externalStatusPlaceholder')}
              </option>
              <option value="help_needed">{t('gest:affichages.diffusion.helpNeeded')}</option>
              <option value="published">{t('gest:affichages.diffusion.published')}</option>
            </select>
          </div>
        ) : null}
        {visibility !== 'internal' ? (
          <div className="flex flex-col gap-2">
            <label htmlFor="edit-posting-external-url" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:affichages.form.externalUrl')}
            </label>
            <input
              id="edit-posting-external-url"
              type="url"
              maxLength={500}
              placeholder={t('gest:affichages.form.externalUrlPlaceholder')}
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              className={`${GS_COMPONENTS.input}`}
            />
            <p className="text-meta text-slate-400 dark:text-slate-500">
              {t('gest:affichages.form.externalUrlHelp')}
            </p>
          </div>
        ) : null}
        <div className="flex flex-col gap-2">
          <label htmlFor="edit-posting-desc" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.description')}
          </label>
          <textarea
            id="edit-posting-desc"
            rows={4}
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`${GS_COMPONENTS.input}`}
          />
        </div>
        <TasksFields tasks={tasks} onChange={setTasks} />
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-posting-deadline" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:affichages.form.deadline')}
            </label>
            <input
              id="edit-posting-deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className={`${GS_COMPONENTS.input} w-48`}
            />
          </div>
          <button type="button" onClick={save} disabled={update.isPending} className={GS_COMPONENTS.btnAction}>
            {t('gest:affichages.edit.save')}
          </button>
        </div>
        {stepError ? (
          <p role="alert" className="text-field text-red-600 dark:text-red-400">{stepError}</p>
        ) : null}
      </div>
    </details>
  );
}
